import { useState, useCallback, useEffect } from "react";
import { 
  SessionState, GamePlayer, Scorecard, PlayerWithScorecard, 
  GameSession, ScoreVisibility, User, GameMode
} from "../types/game";
import { GameSessionActions } from "../types/actions";
import {
  computeScorecardTotal, suggestScores, ScorecardValues
} from "../utils/gameLogic";

// gameLogic only reads category keys, so the extra string fields on a
// Scorecard (player_id, session_id, updated_at) are safely ignored.
const scValues = (sc: Scorecard): ScorecardValues =>
  sc as unknown as ScorecardValues;

const LOCAL_STORAGE_KEY = "poker_dice_local_game";

export function useLocalGame(me: User | null): GameSessionActions {
  const [state, setState] = useState<SessionState | null>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  });
  const [suggestions, setSuggestions] = useState<Record<string, number>>({});

  // Persistence
  useEffect(() => {
    if (state) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }, [state]);

  const createSession = useCallback(async () => {
    const sessionId = crypto.randomUUID();
    const playerId = crypto.randomUUID();
    const cachedName = localStorage.getItem("poker_dice_local_admin_name");
    
    const adminPlayer: GamePlayer = {
      id: playerId,
      session_id: sessionId,
      user_id: me?.id ?? "local-admin",
      telegram_id: me?.telegram_id ?? null,
      display_name: cachedName || me?.first_name || "Player 1",
      avatar: me?.photo_url || "🎲",
      seat_index: 0,
      is_admin: true,
    };

    const emptyScorecard: Scorecard = {
      player_id: playerId,
      session_id: sessionId,
      ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
      one_pair: null, two_pairs: null, three_of_kind: null, straight1: null,
      straight2: null, full_house: null, four_of_kind: null, poker: null, garbage: null,
    };

    const newSession: GameSession = {
      id: sessionId,
      invite_code: "LOCAL",
      status: "setup",
      game_mode: "free",
      score_visibility: "realtime",
      created_by: adminPlayer.user_id!,
      created_at: new Date().toISOString(),
      players: [adminPlayer],
      roll_count: 0,
    };

    const newState: SessionState = {
      session: newSession,
      players_with_scorecards: [{
        player: adminPlayer,
        scorecard: emptyScorecard,
        totals: computeScorecardTotal(scValues(emptyScorecard), scValues(emptyScorecard))
      }]
    };

    setState(newState);
  }, [me]);

  const addPhantomPlayer = useCallback(async (displayName: string, avatar?: string) => {
    setState(prev => {
      if (!prev) return prev;
      const playerId = crypto.randomUUID();
      const newPlayer: GamePlayer = {
        id: playerId,
        session_id: prev.session.id,
        user_id: null,
        telegram_id: null,
        display_name: displayName,
        avatar: avatar ?? "👤",
        seat_index: prev.players_with_scorecards.length,
        is_admin: false,
      };
      const emptySc: Scorecard = {
        player_id: playerId, session_id: prev.session.id,
        ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
        one_pair: null, two_pairs: null, three_of_kind: null, straight1: null,
        straight2: null, full_house: null, four_of_kind: null, poker: null, garbage: null,
      };
      return {
        ...prev,
        session: { ...prev.session, players: [...prev.session.players, newPlayer] },
        players_with_scorecards: [
          ...prev.players_with_scorecards,
          { player: newPlayer, scorecard: emptySc, totals: computeScorecardTotal(scValues(emptySc), scValues(emptySc)) }
        ]
      };
    });
  }, []);

  const startGame = useCallback(async () => {
    setState(prev => {
      if (!prev) return prev;
      const nextPlayer = prev.players_with_scorecards[0].player;
      return {
        ...prev,
        session: { 
          ...prev.session, 
          status: "active",
          current_turn_player_id: prev.session.game_mode === "strict" ? nextPlayer.id : null,
          current_dice: [1,1,1,1,1],
          dice_holds: [false,false,false,false,false],
          roll_count: 0
        }
      };
    });
  }, []);

  const rollDice = useCallback(async () => {
    setState(prev => {
      if (!prev || prev.session.roll_count >= 3) return prev;
      const currentDice = prev.session.current_dice || [1,1,1,1,1];
      const holds = prev.session.dice_holds || [false,false,false,false,false];
      const nextDice = currentDice.map((d, i) => (prev.session.roll_count === 0 || !holds[i] ? Math.floor(Math.random() * 6) + 1 : d));
      
      return {
        ...prev,
        session: {
          ...prev.session,
          current_dice: nextDice,
          roll_count: prev.session.roll_count + 1
        }
      };
    });
  }, []);

  const toggleHold = useCallback(async (index: number) => {
    setState(prev => {
      if (!prev || prev.session.roll_count === 0) return prev;
      const holds = [...(prev.session.dice_holds || [false,false,false,false,false])];
      holds[index] = !holds[index];
      return {
        ...prev,
        session: { ...prev.session, dice_holds: holds }
      };
    });
  }, []);

  const updateScore = useCallback(async (playerId: string, category: string, value: number | null, fromHand: boolean = false) => {
    setState(prev => {
      if (!prev) return prev;

      const newPlayers = prev.players_with_scorecards.map(pwsc => {
        if (pwsc.player.id !== playerId) return pwsc;
        const marks = (pwsc.scorecard.from_hand ?? []).filter(c => c !== category);
        if (fromHand && value !== null) marks.push(category);
        const newSc = { ...pwsc.scorecard, [category]: value, from_hand: marks };
        return {
          ...pwsc,
          scorecard: newSc,
          totals: computeScorecardTotal(scValues(newSc), scValues(newSc))
        };
      });

      let nextPlayerId = prev.session.current_turn_player_id;
      let rollCount = prev.session.roll_count;
      let dice = prev.session.current_dice;
      let holds = prev.session.dice_holds;

      if (prev.session.game_mode === "strict") {
        const currentIndex = prev.players_with_scorecards.findIndex(p => p.player.id === playerId);
        const nextIndex = (currentIndex + 1) % prev.players_with_scorecards.length;
        nextPlayerId = prev.players_with_scorecards[nextIndex].player.id;
        rollCount = 0;
        dice = [1,1,1,1,1];
        holds = [false,false,false,false,false];
      }

      return {
        ...prev,
        session: {
          ...prev.session,
          current_turn_player_id: nextPlayerId,
          roll_count: rollCount,
          current_dice: dice,
          dice_holds: holds
        },
        players_with_scorecards: newPlayers
      };
    });
  }, []);

  const updateSettings = useCallback(async (scoreVisibility?: ScoreVisibility, gameMode?: string) => {
    setState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        session: {
          ...prev.session,
          score_visibility: scoreVisibility ?? prev.session.score_visibility,
          game_mode: (gameMode as GameMode) ?? prev.session.game_mode
        }
      };
    });
  }, []);

  const finishGame = useCallback(async () => {
    setState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        session: { ...prev.session, status: "finished", finished_at: new Date().toISOString() }
      };
    });
  }, []);

  const leaveGame = useCallback(() => {
    setState(null);
  }, []);

  const updatePlayerName = useCallback(async (playerId: string, name: string) => {
    setState(prev => {
        if (!prev) return prev;
        const isAdmin = prev.players_with_scorecards.find(p => p.player.id === playerId)?.player.is_admin;
        if (isAdmin) {
            localStorage.setItem("poker_dice_local_admin_name", name);
        }
        return {
            ...prev,
            players_with_scorecards: prev.players_with_scorecards.map(pwsc => 
                pwsc.player.id === playerId 
                    ? { ...pwsc, player: { ...pwsc.player, display_name: name } }
                    : pwsc
            ),
            session: {
                ...prev.session,
                players: prev.session.players.map(p => 
                    p.id === playerId ? { ...p, display_name: name } : p
                )
            }
        };
    });
  }, []);

  // Suggestions
  useEffect(() => {
    if (state?.session.status === "active" && state.session.current_dice && state.session.roll_count > 0) {
      setSuggestions(suggestScores(state.session.current_dice, state.session.roll_count === 1));
    } else {
      setSuggestions({});
    }
  }, [state?.session.current_dice, state?.session.roll_count, state?.session.status]);

  // Dummy implementations for offline
  const joinByCode = useCallback(async () => {}, []);
  const fetchById = useCallback(async () => {}, []);
  const addRealPlayer = useCallback(async () => {}, []);
  const removePlayer = useCallback(async (pid: string) => {
    setState(prev => {
        if (!prev) return prev;
        return {
            ...prev,
            players_with_scorecards: prev.players_with_scorecards.filter(p => p.player.id !== pid),
            session: {
                ...prev.session,
                players: prev.session.players.filter(p => p.id !== pid)
            }
        };
    });
  }, []);
  const rematchGame = useCallback(async () => {
    // Port of rematch logic: create new session with same players
    if (!state) return;
    const oldPlayers = state.players_with_scorecards;
    await createSession();
    setState(prev => {
        if (!prev) return prev;
        const baseAdmin = prev.players_with_scorecards[0];
        const session_id = prev.session.id;
        const newPlayers: PlayerWithScorecard[] = oldPlayers.map((old, i) => {
            const pid = i === 0 ? baseAdmin.player.id : crypto.randomUUID();
            const p: GamePlayer = { ...old.player, id: pid, session_id };
            const sc: Scorecard = {
                player_id: pid, session_id,
                ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
                one_pair: null, two_pairs: null, three_of_kind: null, straight1: null,
                straight2: null, full_house: null, four_of_kind: null, poker: null, garbage: null,
            };
            return { player: p, scorecard: sc, totals: computeScorecardTotal(scValues(sc), scValues(sc)) };
        });
        return {
            ...prev,
            session: { ...prev.session, players: newPlayers.map(n => n.player) },
            players_with_scorecards: newPlayers
        };
    });
  }, [state, createSession]);

  const sendReaction = useCallback(async () => {}, []);
  const reorderPlayers = useCallback(async (ids: string[]) => {
    setState(prev => {
        if (!prev) return prev;
        const byId = Object.fromEntries(prev.players_with_scorecards.map(p => [p.player.id, p]));
        const reordered = ids.map(id => byId[id]).filter(Boolean);
        return { ...prev, players_with_scorecards: reordered };
    });
  }, []);

  return {
    state,
    loading: false,
    error: null,
    wsStatus: "connected", // Always "connected" in local mode
    queuedCount: 0,
    suggestions,
    createSession,
    joinByCode,
    fetchById,
    addRealPlayer,
    addPhantomPlayer,
    removePlayer,
    updateScore,
    updateSettings,
    finishGame,
    startGame,
    rematchGame,
    rollDice,
    toggleHold,
    sendReaction,
    notifyResults: async () => {},
    reorderPlayers,
    updatePlayerName,
    leaveGame,
  };
}
